import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { FileText, Play, ExternalLink, ArrowLeft, BookOpen, X } from "lucide-react";

import { Navbar } from "@/components/layout/Nav";
import { useLang } from "@/context/LangContext";
import { MATERIALS, materialLabel } from "@/data/materials";

const VideoModal = ({ src, title, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
    onClick={onClose}
    data-testid="library-video-modal"
  >
    <motion.div
      initial={{ scale: 0.9, y: 20 }}
      animate={{ scale: 1, y: 0 }}
      exit={{ scale: 0.9, y: 20 }}
      className="brutal bg-white max-w-4xl w-full"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b-4 border-black bg-canales-yellow">
        <div className="font-display font-black truncate pr-2">{title}</div>
        <button
          data-testid="library-video-close"
          onClick={onClose}
          className="brutal-btn bg-canales-red text-white px-2 py-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <video src={src} controls autoPlay className="w-full block bg-black" style={{ aspectRatio: "16/9" }}>
        <track kind="captions" />
      </video>
    </motion.div>
  </motion.div>
);

const MaterialCard = ({ item, color, lang, onPlay }) => {
  const Icon = item.type === "video" ? Play : FileText;
  const isVideo = item.type === "video";
  return (
    <motion.div
      whileHover={{ y: -3 }}
      className="brutal bg-white p-4 flex flex-col gap-2"
      data-testid={`material-card-${item.type}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 w-11 h-11 flex items-center justify-center border-4 border-black"
          style={{ background: color, color: "white" }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-base leading-tight">{item.title[lang] || item.title.es}</div>
          <div className="text-xs font-body text-neutral-600 mt-0.5 uppercase tracking-widest">
            {item.type === "pdf" ? "PDF" : lang === "es" ? "Video" : "Video"} · {item.lang.toUpperCase()}
          </div>
        </div>
      </div>
      <p className="font-body text-sm text-neutral-700">
        {item.description?.[lang] || item.description?.es}
      </p>
      <div className="mt-1 flex flex-wrap gap-2">
        {isVideo ? (
          <button
            data-testid="material-play-btn"
            onClick={() => onPlay(item)}
            className="brutal-btn bg-black text-white px-3 py-1.5 text-sm inline-flex items-center gap-1"
          >
            <Play className="w-4 h-4" /> {materialLabel(item, lang)}
          </button>
        ) : (
          <a
            data-testid="material-open-btn"
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="brutal-btn bg-canales-blue text-white px-3 py-1.5 text-sm inline-flex items-center gap-1"
          >
            <ExternalLink className="w-4 h-4" /> {materialLabel(item, lang)}
          </a>
        )}
      </div>
    </motion.div>
  );
};

export default function Biblioteca() {
  const { lang } = useLang();
  const [tab, setTab] = useState("all"); // all | vendedor | servicio | financiamiento | allstate
  const [playing, setPlaying] = useState(null);

  const visible = useMemo(() => {
    if (tab === "all") return MATERIALS;
    return MATERIALS.filter((m) => m.slug === tab);
  }, [tab]);

  const totalCount = MATERIALS.reduce((acc, m) => acc + m.items.length, 0);

  return (
    <div className="min-h-screen bg-halftone" data-testid="biblioteca-root">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-10">
        <Link
          to="/"
          className="brutal-btn bg-white px-3 py-1 text-sm inline-flex items-center gap-1 mb-4"
          data-testid="biblioteca-back"
        >
          <ArrowLeft className="w-4 h-4" /> {lang === "es" ? "Inicio" : "Home"}
        </Link>

        {/* Hero */}
        <div className="brutal p-6 text-white flex items-center gap-4 flex-wrap" style={{ background: "#005BBB" }}>
          <div className="w-16 h-16 border-4 border-black bg-canales-yellow text-black flex items-center justify-center">
            <BookOpen className="w-8 h-8" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="inline-flex items-center gap-2 bg-canales-yellow text-black px-3 py-1 rounded-full border-2 border-black font-display font-bold text-xs uppercase tracking-widest">
              {lang === "es" ? "Biblioteca Canales" : "Canales Library"}
            </div>
            <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl">
              {lang === "es" ? "Todo tu entrenamiento en un solo lugar" : "All your training in one place"}
            </h1>
            <p className="font-body opacity-95 mt-1">
              {lang === "es"
                ? `${totalCount} materiales · PDFs y videos organizados por rol.`
                : `${totalCount} materials · PDFs and videos organized by role.`}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="biblioteca-tabs">
          {[
            { id: "all", label: { es: "Todos", en: "All" }, color: "#111" },
            ...MATERIALS.map((m) => ({ id: m.slug, label: m.label, color: m.color })),
          ].map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`biblioteca-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`brutal-btn px-4 py-1.5 text-sm font-display font-bold ${active ? "text-white" : "bg-white text-black"}`}
                style={active ? { background: t.color } : undefined}
              >
                {t.label[lang] || t.label.es}
              </button>
            );
          })}
        </div>

        {/* Sections */}
        <div className="mt-6 space-y-8">
          {visible.map((section) => (
            <div key={section.slug} className="brutal p-5 bg-white">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-3 h-8 border-2 border-black"
                  style={{ background: section.color }}
                />
                <h2 className="font-display font-black text-2xl">
                  {section.label[lang] || section.label.es}
                </h2>
                <span className="text-sm font-body text-neutral-600">({section.items.length})</span>
                {section.comic_slug && (
                  <Link
                    to={`/comics/${section.comic_slug}`}
                    className="ml-auto brutal-btn bg-canales-yellow text-black px-3 py-1 text-xs inline-flex items-center gap-1"
                    data-testid={`biblioteca-comic-${section.slug}`}
                  >
                    <BookOpen className="w-3.5 h-3.5" /> {lang === "es" ? "Ver cómic" : "Open comic"}
                  </Link>
                )}
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.items.map((it, i) => (
                  <MaterialCard
                    key={i}
                    item={it}
                    color={section.color}
                    lang={lang}
                    onPlay={setPlaying}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {playing && (
          <VideoModal
            src={playing.url}
            title={playing.title[lang] || playing.title.es}
            onClose={() => setPlaying(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
